import { BEIGE_600, GRAY_400, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'
import { Box, Stack, Typography } from '@mui/material'
import { MANUAL_EDIT_TOOLS, getManualToolIcon } from '../../PhotoStudioUtils'

import { AnalyticsEvent } from 'utils/analytics'
import { EditionType } from '../photoStudioTypes'
import { FC } from 'react'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIButton } from 'components/common/MUIButton'
import { externalNavigate } from 'utils/helpers'
import { getPricingUrl } from 'utils/localization'
import i18n from 'translations/i18n'
import { useClientPhotoStudio } from '../ClientPhotoStudio.context'
import { useTranslation } from 'react-i18next'

/**
 * Upsell component for non-subscribers in Photo Studio Adjust Panel
 * Displays a promotional section encouraging users to subscribe to access Adjust tools
 * @example <NonSubscriberAdjustUpsell logPhotoStudioEvent={logPhotoStudioEvent} />
 */
export const NonSubscriberAdjustUpsell: FC = () => {
  const { t } = useTranslation(['photo_studio_editing', 'common'])
  const { logPhotoStudioEvent } = useClientPhotoStudio()

  const handleSubscribeClick = () => {
    logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_SAAS_UPSELL, { editionType: EditionType.ADJUST })
    externalNavigate(getPricingUrl(i18n.language))
  }

  return (
    <Box
      sx={{ backgroundColor: BEIGE_600, borderRadius: '.8rem', height: 'fit-content' }}
    >
      <Stack sx={{ padding: 1.6 }}>

        <MUIBadge
          size="xs"
          color="coral"
          icon={<LockOutlinedIcon fontSize="small" />}
          label={t('common:Subscribers_only')}
          sx={{ marginBottom: 1.2, width: 'fit-content' }}
        />

        <Stack gap=".4rem" sx={{ marginBottom: 1.6 }}>
          <Typography variant="text-sm" fontWeight="600" color={GRAY_900}>
            {t('non_subscribers_title')}
          </Typography>
          <Typography variant="text-xs" fontWeight="400" color={GRAY_700} marginTop={0.6}>
            {t('non_subscribers_description')}
          </Typography>
        </Stack>

        <MUIButton
          sx={{ marginTop: 1, marginBottom: '2.4rem', fontSize: '12px', width: 'fit-content' }}
          size="sm"
          onClick={handleSubscribeClick}
        >
          {t('subscribe_to_bkbn_button')}
        </MUIButton>

        <Stack justifyContent="flex-start" alignItems="stretch" spacing=".8rem" sx={{ width: '100%' }}>
          {Object.values(MANUAL_EDIT_TOOLS).map((tool) =>
            <Box
              key={tool}
              sx={{
                backgroundColor: WHITE,
                height: '4.4rem',
                gap: '.8rem',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-start',
                padding: '1.2rem',
                borderRadius: '.8rem',
                border: '1px solid',
                borderColor: GRAY_400,
                color: GRAY_700,
                textAlign: 'center',
                fontWeight: '400',
                cursor: 'not-allowed',
                opacity: 1,
              }}
            >
              {getManualToolIcon(tool)}
              <Typography variant="text-sm" color={GRAY_700} fontWeight={350}>
                {t(`manual_tools.${tool}`)}
              </Typography>
            </Box>
          )
          }
        </Stack>

      </Stack>
    </Box>
  )
} 
